import React, { useState, useCallback } from 'react';
import styles from './WasteFactorList.module.css';
import type { WasteFactorRecord } from '../../hooks/useTraining';
import { BulkImportModal, WASTE_FACTOR_COLUMNS } from './BulkImportModal';

export interface WasteFactorListProps {
  wasteFactors: WasteFactorRecord[];
  onCreateWasteFactor?: (data: Record<string, unknown>) => Promise<void>;
  onUpdateWasteFactor?: (id: number, data: Record<string, unknown>) => Promise<void>;
  onDeleteWasteFactor?: (id: number) => Promise<void>;
  onBulkCreate?: (records: Record<string, unknown>[]) => Promise<unknown>;
}

interface EditState {
  materialType: string;
  pattern: string;
  factor: string;
  notes: string;
}

export const WasteFactorList: React.FC<WasteFactorListProps> = ({
  wasteFactors,
  onCreateWasteFactor,
  onUpdateWasteFactor,
  onDeleteWasteFactor,
  onBulkCreate,
}) => {
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [editState, setEditState] = useState<EditState>({ materialType: '', pattern: '', factor: '', notes: '' });
  const [showImport, setShowImport] = useState(false);
  const isAdmin = !!(onCreateWasteFactor || onUpdateWasteFactor || onDeleteWasteFactor);

  const startEdit = useCallback((wf: WasteFactorRecord) => {
    setEditingId(wf.id);
    setEditState({
      materialType: wf.materialType,
      pattern: wf.pattern,
      factor: (wf.factor * 100).toFixed(1),
      notes: wf.notes,
    });
  }, []);

  const startCreate = useCallback(() => {
    setEditingId('new');
    setEditState({ materialType: '', pattern: '', factor: '10', notes: '' });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
  }, []);

  const saveEdit = useCallback(async () => {
    const data = {
      materialType: editState.materialType,
      pattern: editState.pattern,
      factor: parseFloat(editState.factor) / 100,
      notes: editState.notes,
    };

    if (editingId === 'new') {
      if (onCreateWasteFactor) await onCreateWasteFactor(data);
    } else if (editingId !== null) {
      if (onUpdateWasteFactor) await onUpdateWasteFactor(editingId, data);
    }
    setEditingId(null);
  }, [editingId, editState, onCreateWasteFactor, onUpdateWasteFactor]);

  const handleDelete = useCallback(async (wf: WasteFactorRecord) => {
    if (!onDeleteWasteFactor) return;
    if (!window.confirm(`Delete waste factor for "${wf.materialType}"?`)) return;
    await onDeleteWasteFactor(wf.id);
  }, [onDeleteWasteFactor]);

  const handleFieldChange = (field: keyof EditState, value: string) => {
    setEditState(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Waste Factors</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={styles.count}>{wasteFactors.length} factors</span>
          {isAdmin && (
            <>
              <button className={styles.adminBtn} onClick={startCreate}>
                + Add Factor
              </button>
              {onBulkCreate && (
                <button className={styles.adminBtn} onClick={() => setShowImport(true)}>
                  Import CSV/XLSX
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showImport && onBulkCreate && (
        <BulkImportModal
          resourceName="Waste Factors"
          columns={WASTE_FACTOR_COLUMNS}
          onImport={onBulkCreate}
          onClose={() => setShowImport(false)}
        />
      )}

      <div className={styles.tableWrap}>
        {wasteFactors.length === 0 && editingId !== 'new' && (
          <div className={styles.emptyState}>No waste factors configured yet</div>
        )}

        {(wasteFactors.length > 0 || editingId === 'new') && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Material Type</th>
                <th>Pattern</th>
                <th>Factor</th>
                <th>Notes</th>
                {isAdmin && <th style={{ width: 120 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {editingId === 'new' && (
                <tr className={styles.editRow}>
                  <td>
                    <input className={styles.inlineInput} value={editState.materialType} onChange={(e) => handleFieldChange('materialType', e.target.value)} placeholder="e.g., tile" autoFocus />
                  </td>
                  <td>
                    <input className={styles.inlineInput} value={editState.pattern} onChange={(e) => handleFieldChange('pattern', e.target.value)} placeholder="e.g., straight lay" />
                  </td>
                  <td>
                    <input className={styles.inlineInput} type="number" step="0.1" value={editState.factor} onChange={(e) => handleFieldChange('factor', e.target.value)} style={{ width: 70 }} />
                  </td>
                  <td>
                    <input className={styles.inlineInput} value={editState.notes} onChange={(e) => handleFieldChange('notes', e.target.value)} placeholder="Notes" />
                  </td>
                  <td>
                    <div className={styles.inlineActions}>
                      <button className={`${styles.adminBtnSmall} ${styles.adminBtnSave}`} onClick={saveEdit}>Save</button>
                      <button className={styles.adminBtnSmall} onClick={cancelEdit}>Cancel</button>
                    </div>
                  </td>
                </tr>
              )}

              {wasteFactors.map((wf) => (
                editingId === wf.id ? (
                  <tr key={wf.id} className={styles.editRow}>
                    <td>
                      <input className={styles.inlineInput} value={editState.materialType} onChange={(e) => handleFieldChange('materialType', e.target.value)} />
                    </td>
                    <td>
                      <input className={styles.inlineInput} value={editState.pattern} onChange={(e) => handleFieldChange('pattern', e.target.value)} />
                    </td>
                    <td>
                      <input className={styles.inlineInput} type="number" step="0.1" value={editState.factor} onChange={(e) => handleFieldChange('factor', e.target.value)} style={{ width: 70 }} />
                    </td>
                    <td>
                      <input className={styles.inlineInput} value={editState.notes} onChange={(e) => handleFieldChange('notes', e.target.value)} />
                    </td>
                    <td>
                      <div className={styles.inlineActions}>
                        <button className={`${styles.adminBtnSmall} ${styles.adminBtnSave}`} onClick={saveEdit}>Save</button>
                        <button className={styles.adminBtnSmall} onClick={cancelEdit}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={wf.id}>
                    <td>{wf.materialType}</td>
                    <td>{wf.pattern}</td>
                    <td className={styles.factorCell}>{(wf.factor * 100).toFixed(1)}%</td>
                    <td className={styles.notesCell}>{wf.notes}</td>
                    {isAdmin && (
                      <td>
                        <div className={styles.inlineActions}>
                          <button className={styles.adminBtnSmall} onClick={() => startEdit(wf)}>Edit</button>
                          <button className={`${styles.adminBtnSmall} ${styles.adminBtnDanger}`} onClick={() => handleDelete(wf)}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
